import { Resource, ResourceRepository } from '@telespot/domain';

export interface RetrieveResourceChildrenOutput {
  id: string;
  name: string;
}

/**
 * Error codes mirror `RetrieveResourceDetailErrorCode`: a stable namespaced
 * identifier callers can disambiguate without parsing free-form messages.
 */
export enum RetrieveResourceChildrenErrorCode {
  REPOSITORY_ERROR = 'retrieve_resource_children.repository_error',
}

/**
 * Thrown when retrieving a resource's children fails.
 *
 * `details` carries structured context (e.g. `{ resourceId }`) so callers
 * can render or log the failure without scraping the message.
 */
export class RetrieveResourceChildrenException extends Error {
  constructor(
    public readonly code: RetrieveResourceChildrenErrorCode,
    public readonly details: Record<string, string> = {}
  ) {
    super(code.toString());
    this.name = 'RetrieveResourceChildrenException';
    Object.setPrototypeOf(this, RetrieveResourceChildrenException.prototype);
  }
}

export interface IRetrieveResourceChildrenQuery {
  /**
   * Returns the direct children of a resource container as flat `{ id, name }` items.
   * The resource read hydrates a single nesting level, so descent re-queries per node.
   * @throws RetrieveResourceChildrenException when the repository read fails.
   */
  execute(resourceId: string): Promise<RetrieveResourceChildrenOutput[]>;
}

export class RetrieveResourceChildrenQuery implements IRetrieveResourceChildrenQuery {
  constructor(private readonly resourceRepository: ResourceRepository) {}

  public async execute(resourceId: string): Promise<RetrieveResourceChildrenOutput[]> {
    let resource: Resource | undefined;
    try {
      resource = await this.resourceRepository.getById(resourceId);
    } catch {
      throw new RetrieveResourceChildrenException(RetrieveResourceChildrenErrorCode.REPOSITORY_ERROR, {
        resourceId,
      });
    }
    // A missing resource is treated as "no children" (not an error): the tree
    // node is already known to the caller; there is just nothing to descend into.
    return (resource?.resources ?? []).map((child: Resource) => ({
      id: child.id,
      name: child.name,
    }));
  }
}
